/**
 * @file This file contains crafting recipes for the "Crafting Mode".
 * @module craftingRecipesConfig
 */

/**
 * An array of item keys that can appear in the queue for Crafting Mode.
 * @type {string[]}
 */
export const craftingQueueItemKeys = [
    'c_seed', 'c_seed', 'c_seed', 
    'c_water', 'c_water', 
    'c_rock', 'c_rock', 'c_rock'
];
/**
 * @typedef {Object} CraftingRecipe
 * @property {string} key - A unique key for the recipe (e.g., 'make_planks').
 * @property {Object.<string, number>} ingredients - An object where keys are item keys and values are the required count.
 * @property {string[]} [tools] - An array of item keys that are required but not consumed.
 * @property {string} result - The item key of the crafted item.
 * @property {number} [score=50] - The score awarded for this craft.
 */

/**
 * An array of all available crafting recipes.
 * The system will check for these patterns when an item is placed.
 * The order matters: the first recipe in the list that matches a group will be used.
 * @type {CraftingRecipe[]}
 */
export const craftingRecipes = [
    // --- Tier 1 Recipes (Base Ingredients) ---
    {
        key: 'grow_tree',
        ingredients: { 'c_seed': 1, 'c_water': 1 },
        result: 'c_tree',
        score: 20
    },
    {
        key: 'make_flint',
        ingredients: { 'c_rock': 2 },
        result: 'c_flint',
        score: 20
    },

    // --- Tier 2 Recipes (Using Tier 1 Products) ---
    {
        key: 'harvest_logs',
        ingredients: { 'c_tree': 2 },
        result: 'c_log',
        score: 40
    },
    {
        key: 'make_axe',
        ingredients: { 'c_log': 1, 'c_flint': 2 },
        result: 'c_axe',
        score: 60
    },
    {
        key: 'make_fire',
        ingredients: { 'c_log': 1, 'c_flint': 1 },
        result: 'c_fire',
        score: 50
    },

    // --- Tier 3 Recipes (Interconnected Paths) ---
    {
        key: 'lay_foundation',
        ingredients: { 'c_rock': 2 },
        tools: ['c_axe'],
        result: 'c_foundation',
        score: 120
    },
    {
        key: 'make_planks',
        ingredients: { 'c_log': 1 },
        tools: ['c_axe'],
        result: 'c_plank',
        score: 80
    },
    {
        key: 'make_campfire',
        ingredients: { 'c_fire': 1, 'c_log': 2 },
        result: 'c_campfire',
        score: 100
    },

    // --- Tier 4 Recipes (Advanced Building) ---
    {
        key: 'build_hut',
        ingredients: { 'c_plank': 2, 'c_log': 1 },
        result: 'c_hut',
        score: 200
    },
    {
        key: 'build_house',
        ingredients: { 'c_hut': 1, 'c_foundation': 1 },
        result: 'c_house',
        score: 300
    },
    {
        key: 'build_mansion',
        ingredients: { 'c_house': 2, 'c_tree': 1 },
        result: 'c_mansion',
        score: 500
    },
    {
        key: 'build_inn',
        ingredients: { 'c_house': 1, 'c_campfire': 1 },
        result: 'c_inn',
        score: 400
    }
];